import { useMemo } from 'react';

/**
 * Above this many options a dropdown renders nothing until the user searches. One node per option
 * is ~19k React mounts for the pincode fields, which freezes the menu on open. Deliberately above
 * the 1,058-entry city list so every existing caller keeps its current behaviour.
 */
export const SEARCH_GATE_THRESHOLD = 2000;
export const MIN_SEARCH_CHARS = 2;
export const MAX_RENDERED_OPTIONS = 100;

/**
 * Gating and capping only — filtering stays with the caller, which already owns it (and its own
 * ordering rules). Takes the total option count and the caller's already-filtered list.
 */
export function useSearchGatedOptions<T>(totalCount: number, filtered: T[], searchText: string) {
    const isLargeList = totalCount > SEARCH_GATE_THRESHOLD;
    const searchGateActive = isLargeList && searchText.trim().length < MIN_SEARCH_CHARS;

    // The cap applies ONLY to large lists. Applying it unconditionally would silently truncate
    // every existing caller — the 1,058-entry city list would have rendered 100 options.
    const renderedOptions = useMemo(() => {
        if (searchGateActive) return [];
        return isLargeList ? filtered.slice(0, MAX_RENDERED_OPTIONS) : filtered;
    }, [searchGateActive, isLargeList, filtered]);

    return {
        isLargeList,
        searchGateActive,
        renderedOptions,
        unrenderedMatchCount: filtered.length - renderedOptions.length,
        // The hint belongs in the field, not as a block in an otherwise empty option area. It is
        // visible exactly while the gate is closed, which is when it is needed.
        searchPlaceholder: isLargeList ? `Type at least ${MIN_SEARCH_CHARS} characters (${totalCount.toLocaleString()} options)` : 'Search',
    };
}
